import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Logo } from "./Logo";
import { Menu, X, ArrowRight } from "lucide-react";

export const Nav = () => {
    const { user, logout, t } = useApp();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);

    const links = [
        { to: "/how-it-works", label: t("nav.how") },
        { to: "/how-it-works", label: "Astrology & Psychology", key: "astro" },
        { to: "/about", label: t("nav.about") },
        { to: "/pricing", label: t("nav.pricing") },
        { to: "/safety", label: t("nav.safety") },
        { to: "/contact", label: t("nav.contact") },
    ];

    const authedLinks = user ? [
        { to: "/assessment", label: t("nav.assessment") },
        { to: "/companion", label: t("nav.companion") },
        { to: "/report", label: t("nav.report") },
    ] : [];

    const go = (to) => { setOpen(false); nav(to); };
    const dark = loc.pathname === "/about";
    const txt = dark ? "text-[#F5F0E6]/80" : "text-charcoal/80";
    const hov = dark ? "hover:text-[#E2C089]" : "hover:text-forest";
    const act = dark ? "text-[#F5F0E6] font-semibold border-b border-[#E2C089] pb-0.5" : "text-forest font-semibold";

    return (
        <header className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b ${dark ? "bg-[#0B1511]/90 border-white/10" : "bg-ivory/85 border-charcoal/10"}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3.5 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo" className="text-xl"><Logo light={dark} /></Link>

                <nav className={`hidden lg:flex items-center gap-7 text-sm ${txt}`}>
                    {[...links, ...authedLinks].map((l) => (
                        <Link key={l.key || l.to} to={l.to} data-testid={`nav-${l.key || l.to.slice(1)}`}
                            className={`${hov} transition-colors ${loc.pathname === l.to && !l.key ? act : ""}`}>
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3">
                    {user && user.role === "admin" && (
                        <Link to="/admin" data-testid="nav-admin" className="hidden sm:inline text-sm text-forest font-semibold">{t("nav.admin")}</Link>
                    )}
                    {user ? (
                        <>
                            <Link to="/profile" data-testid="nav-profile" className={`hidden sm:inline text-sm ${txt} ${hov}`}>{t("nav.profile")}</Link>
                            <button data-testid="nav-logout" onClick={() => { logout(); nav("/"); }}
                                className={`hidden sm:inline text-sm ${txt} ${hov}`}>{t("nav.logout")}</button>
                        </>
                    ) : (
                        <Link to="/login" data-testid="nav-login" className={`hidden sm:inline text-sm px-4 py-1.5 rounded-full border transition-colors ${dark ? "border-[#F5F0E6]/30 text-[#F5F0E6] hover:bg-[#F5F0E6]/10" : "border-[#1F3A30]/30 text-[#1F3A30] hover:bg-[#1F3A30] hover:text-[#F5F0E6]"}`}>{t("nav.login")}</Link>
                    )}
                    <button data-testid="nav-start" onClick={() => go(user ? "/assessment" : "/onboarding")}
                        className={`hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm font-medium ${dark ? "rounded-full bg-[#F0DDB0] text-[#24210F]" : "btn-primary"}`}>{t("nav.start")}{dark && <ArrowRight size={14} />}</button>
                    <button data-testid="nav-mobile-toggle" className={`lg:hidden p-2 ${dark ? "text-[#F5F0E6]" : ""}`} onClick={() => setOpen(!open)}>
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-charcoal/10 bg-ivory px-4 py-4 space-y-2" data-testid="mobile-menu">
                    {[...links, ...authedLinks].map((l) => (
                        <button key={l.key || l.to} onClick={() => go(l.to)} className="block w-full text-left py-2 text-charcoal/80">{l.label}</button>
                    ))}
                    {user && user.role === "admin" && <button onClick={() => go("/admin")} className="block w-full text-left py-2 text-forest font-semibold">{t("nav.admin")}</button>}
                    {user ? (
                        <>
                            <button onClick={() => go("/profile")} className="block w-full text-left py-2">{t("nav.profile")}</button>
                            <button onClick={() => { logout(); go("/"); }} className="block w-full text-left py-2 text-charcoal/70">{t("nav.logout")}</button>
                        </>
                    ) : (
                        <button onClick={() => go("/login")} className="block w-full text-left py-2">{t("nav.login")}</button>
                    )}
                    <button onClick={() => go(user ? "/assessment" : "/onboarding")} className="btn-primary w-full px-4 py-2.5 text-sm font-medium mt-2">{t("nav.start")}</button>
                </div>
            )}
        </header>
    );
};

export default Nav;
